import { Button, Dropdown, message } from 'antd';
import React, { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { doDeleteLicense, doGetLicensesList } from '../../api/licenses';
import { LicenseI, LicenseSearchParamsT } from '../../interfaces/license';
import DangerButton from '../../shared/components/buttons/DangerButton';
import PrimaryButton from '../../shared/components/buttons/PrimaryButton';
import LicensesTable from '../../shared/components/tables/Licenses';
import { PAGE_SIZE } from '../../shared/constants';
import AddCircleOutlineIcon from '../../shared/svgs/AddCircleOutlineIcon';
import { CurrentUserI } from '../../interfaces/auth';
import { getNestedQueryValues, handleAccessControl, handleFilterParams } from '../../utils/global';
import LicensesFilterForm from '../../shared/components/forms/license/Filter';

const LicensesPage: React.FC = () => {
  const currentUser: CurrentUserI = useOutletContext();
  const access = handleAccessControl(currentUser.roles, 'licenses');
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const urlSearch = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const queryParams = useMemo(() => {
    const start = Number(urlSearch.get('start')) ?? 0;
    const count = Number(urlSearch.get('count')) !== 0 ? Number(urlSearch.get('count')) : PAGE_SIZE;
    const customerFilter = urlSearch.get('filter[customerId]');
    const filmFilter = urlSearch.get('filter[filmId]');
    const territoriesFilter = getNestedQueryValues(urlSearch, 'filter[territories]');
    const rightIdsFilter = getNestedQueryValues(urlSearch, 'filter[rightIds]');
    const orderBy = urlSearch.get('orderBy');
    const orderType = urlSearch.get('orderType');
    return {
      start,
      count,
      filter: {
        customerId: customerFilter ? Number(customerFilter) : undefined,
        filmId: filmFilter ? Number(filmFilter) : undefined,
        territories: territoriesFilter,
        rightIds: rightIdsFilter,
      },
      orderBy,
      orderType,
    };
  }, [urlSearch]) as LicenseSearchParamsT;
  const { data, isLoading } = useQuery(['licenses', 'list', queryParams], () => doGetLicensesList(queryParams), {
    refetchOnMount: true,
  });

  const currentPage = queryParams.start ? Math.round((Number(queryParams.start) - 1) / PAGE_SIZE + 1) : 1;
  const pagination: any = {
    current: currentPage,
    pageSize: PAGE_SIZE,
    total: data?.total || 0,
    showTotal: (total: string) => `${total} Items`,
  };

  const setQueryParams = (params: any) => {
    const query = handleFilterParams(params);
    navigate(`?${query}`);
  };

  const handleFilter = (params: any) => {
    const currentPage = params.page;
    const offset = currentPage ? (currentPage === 1 ? 0 : (Number(currentPage) - 1) * PAGE_SIZE) : 0;
    const newQueryParams: any = { ...queryParams, start: offset, count: params.per_page };

    Object.keys(params).forEach((key) => {
      if (key.startsWith('sorts[')) {
        const fieldName = key.replace('sorts[', '').replace(']', '');
        const sortType = params[key].toUpperCase();
        newQueryParams.orderBy = fieldName;
        newQueryParams.orderType = sortType;
      }
    });
    setQueryParams(newQueryParams);
  };

  const { mutateAsync: mutationDelete } = useMutation(doDeleteLicense, {
    onSuccess: async () => {
      message.info('Deleted!');
      queryClient.invalidateQueries({
        queryKey: ['licenses', 'list'],
      });
    },
  });

  const handleClickDelete = (license: LicenseI) => {
    mutationDelete(license.id);
  };

  const handleFilterSubmit = (values: any) => {
    setQueryParams({ ...queryParams, filter: { ...queryParams.filter, ...values } });
  };

  const handleResetFilter = () => {
    setQueryParams({ start: 0, count: PAGE_SIZE, filter: undefined });
  };

  const withAction = (record: any) => {
    return (
      <span className="action" style={{ display: 'flex' }}>
        <Dropdown.Button
          overlayClassName="table-action-menu"
          menu={{
            items: [
              access.isCanRead
                ? {
                    key: '1',
                    label: (
                      <Link to={`/licenses/${record?.id}`}>
                        <Button className="btn-table-action-primary" type="link">
                          View
                        </Button>
                      </Link>
                    ),
                  }
                : null,
              access.isCanUpdate
                ? {
                    key: '2',
                    label: (
                      <Link to={`/licenses/${record?.id}/edit`}>
                        <Button className="btn-table-action-primary" type="link">
                          Edit
                        </Button>
                      </Link>
                    ),
                  }
                : null,
              access.isCanDelete
                ? {
                    key: '3',
                    label: <DangerButton label="Delete" onOk={() => handleClickDelete(record)} />,
                  }
                : null,
            ],
          }}
        ></Dropdown.Button>
      </span>
    );
  };

  return (
    <div className="licenses-container">
      <div className="page-container">
        <div className="page-header">
          <div></div>
          <div className="page-header-actions">
            {access.isCanCreate && (
              <Link to="/licenses/new">
                <PrimaryButton label="Create License Sold" icon={<AddCircleOutlineIcon />} />
              </Link>
            )}
          </div>
        </div>
        <div className="page-content">
          <LicensesFilterForm
            handleFilter={handleFilterSubmit}
            handleReset={handleResetFilter}
            queryParams={queryParams}
          />
          <LicensesTable
            rowKey="id"
            loading={isLoading}
            pagination={pagination}
            dataSource={data?.data}
            withAction={withAction}
            onChange2={handleFilter}
            query={queryParams}
          />
        </div>
      </div>
    </div>
  );
};

export default LicensesPage;
